import { ButtonType, ButtonWidget, Img, InteractionState } from '@tableau/widgets';
import React from 'react';
import _ from 'underscore';

import { Messages } from '~src/codegen/Localize';
import { imgPath } from '~src/common/utilities/ImgUrl';

import { FavoriteToggle } from '../../../common/react/components/FavoriteToggle/FavoriteToggle';
import { FavoriteToggleWithCounts } from '../../../common/react/components/FavoriteToggle/FavoriteToggleWithCounts';
import { FavoriteToggleWithLocalState } from '../../../common/react/components/FavoriteToggle/FavoriteToggleWithLocalState';
import { SpaceInfoItemType } from '../../../ts/models/ContentType';
import { ISpaceItemDetailInfo } from '../../../ts/models/ISpaceItemDetailInfo';
import { ActionsMenu } from '../ActionsMenu/ActionsMenu';
import { DetailButton } from './DetailButton';

// The subset of SpaceInfoItemType whose ISpaceItemDetailInfo<C> has all the attributes needed
// to play nice with the below widgets
type HeaderIconSpaceInfoItemType = 'workbook' | 'datasource' | 'flow' | 'project' | 'dataRole' | 'lens' | 'publishedConnection' | 'publishedConnectionTable' | 'collection';

export function FavoriteToggleIcon<C extends HeaderIconSpaceInfoItemType>(params: {
  readonly item: ISpaceItemDetailInfo<C>;
  readonly addToFavorites: () => void;
  readonly removeFromFavorites: () => void;
}): JSX.Element {
  const { item, addToFavorites, removeFromFavorites } = params;

  return <FavoriteToggle
    isTabbable={true}
    isFavorite={item.favorite}
    onAddFavorite={addToFavorites}
    onRemoveFavorite={removeFromFavorites}
  />;
}

export function FavoriteToggleWithCountsIcon<C extends HeaderIconSpaceInfoItemType>(params: {
  readonly item: ISpaceItemDetailInfo<C>;
  readonly addToFavorites: () => void;
  readonly removeFromFavorites: () => void;
}): JSX.Element {
  const { item, addToFavorites, removeFromFavorites } = params;
  const usageInfo = item.usageInfo;

  // If its a flow, it does not have usageInfo implemented yet
  if (item.contentType === 'flow') {
    return <FavoriteToggleWithLocalState
      isTabbable={true}
      isFavorite={item.favorite}
      onAddFavorite={addToFavorites}
      onRemoveFavorite={removeFromFavorites}
    />;
  }
  // in case an older server is connecting to newer front-end
  if (_.isUndefined(usageInfo) || !usageInfo.hasOwnProperty('favoritesTotal')) {
    return FavoriteToggleIcon(params);
  }

  return <FavoriteToggleWithCounts
    canHaveKeyboardFocus={true}
    isFavorite={item.favorite}
    onAddFavorite={addToFavorites}
    onRemoveFavorite={removeFromFavorites}
    favoritesCount={usageInfo.favoritesTotal}
  />;
}

export function DetailButtonIcon<C extends SpaceInfoItemType>(
  item: ISpaceItemDetailInfo<C>,
  openDetail: () => void,
): JSX.Element {
  return <DetailButton testId={`${item.contentType}-${item.id}`} onClick={openDetail}/>;
}

export function ActionsMenuIcon<C extends HeaderIconSpaceInfoItemType>(
  item: ISpaceItemDetailInfo<C>,
  disabled?: boolean,
  buttonTitle?: string,
): JSX.Element {
  return <ActionsMenu buttonTitle={buttonTitle} targetItems={[ item ]} useIcon={true} disabled={disabled}/>;
}

export interface ShareButtonProps {
  readonly onClick: () => void;
  readonly testId: string;
}

export function ShareButton(props: ShareButtonProps) {
  return (
    <ButtonWidget
      buttonType={ButtonType.Icon}
      getContent={ToolbarShareIcon}
      onClick={props.onClick}
      testId={props.testId}
      title={Messages.shareButtonCollection()}
    />
  );
}

const SHARE = imgPath('share_18_large.svg');

function ToolbarShareIcon(interactiveState: InteractionState) {
return (
    <Img
      draggable={false}
      src={SHARE}
    />
  );
}
